import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/auth/Login";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import Structure from "@/pages/admin/Structure";
import People from "@/pages/admin/People";
import Records from "@/pages/admin/Records";
import MarkAttendance from "@/pages/teacher/MarkAttendance";
import TeacherHistory from "@/pages/teacher/TeacherHistory";
import ClockInOut from "@/pages/teacher/ClockInOut";
import TeacherLeave from "@/pages/teacher/Leave";
import StudentDashboard from "@/pages/student/StudentDashboard";
import StudentAppeals from "@/pages/student/Appeals";
import TeacherAppeals from "@/pages/teacher/Appeals";
import AppealsAdmin from "@/pages/admin/AppealsAdmin";
import ParentDashboard from "@/pages/parent/ParentDashboard";
import StaffAttendance from "@/pages/admin/StaffAttendance";
import StaffSettings from "@/pages/admin/StaffSettings";
import LeaveAdmin from "@/pages/admin/LeaveAdmin";
import Notices from "@/pages/shared/Notices";
import Events from "@/pages/shared/Events";
import DailyReports from "@/pages/shared/DailyReports";

const HOME_BY_ROLE = { admin: "/admin", teacher: "/teacher", student: "/student", parent: "/parent" };

function Protected({ roles, allowHod = false, children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="auth-loading">
        <div className="h-8 w-8 border-2 border-slate-950 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (user === false) return <Navigate to="/login" state={{ from: location }} replace />;
  const allowed = (!roles || roles.includes(user.role)) || (allowHod && user.role === "teacher" && user.is_hod);
  if (!allowed) return <Navigate to={HOME_BY_ROLE[user.role] || "/login"} replace />;
  return children;
}

function RoleRedirect() {
  const { user } = useAuth();
  if (user === null) return null;
  if (user === false) return <Navigate to="/login" replace />;
  return <Navigate to={HOME_BY_ROLE[user.role] || "/login"} replace />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<RoleRedirect />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/admin" element={<Protected roles={["admin"]}><AdminDashboard /></Protected>} />
            <Route path="/admin/structure" element={<Protected roles={["admin"]}><Structure /></Protected>} />
            <Route path="/admin/people" element={<Protected roles={["admin"]}><People /></Protected>} />
            <Route path="/admin/records" element={<Protected roles={["admin"]} allowHod><Records /></Protected>} />
            <Route path="/teacher" element={<Protected roles={["teacher"]}><MarkAttendance /></Protected>} />
            <Route path="/teacher/history" element={<Protected roles={["teacher"]}><TeacherHistory /></Protected>} />
            <Route path="/teacher/clock" element={<Protected roles={["teacher"]}><ClockInOut /></Protected>} />
            <Route path="/teacher/leave" element={<Protected roles={["teacher"]}><TeacherLeave /></Protected>} />
            <Route path="/admin/staff-attendance" element={<Protected roles={["admin"]} allowHod><StaffAttendance /></Protected>} />
            <Route path="/admin/staff-settings" element={<Protected roles={["admin"]}><StaffSettings /></Protected>} />
            <Route path="/admin/leave" element={<Protected roles={["admin"]} allowHod><LeaveAdmin /></Protected>} />
            <Route path="/admin/appeals" element={<Protected roles={["admin"]}><AppealsAdmin /></Protected>} />
            <Route path="/teacher/appeals" element={<Protected roles={["teacher"]}><TeacherAppeals /></Protected>} />
            <Route path="/student/appeals" element={<Protected roles={["student"]}><StudentAppeals /></Protected>} />
            <Route path="/daily-reports" element={<Protected roles={["admin", "teacher"]}><DailyReports /></Protected>} />
            <Route path="/notices" element={<Protected><Notices /></Protected>} />
            <Route path="/events" element={<Protected><Events /></Protected>} />
            <Route path="/student" element={<Protected roles={["student"]}><StudentDashboard /></Protected>} />
            <Route path="/parent" element={<Protected roles={["parent"]}><ParentDashboard /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="bottom-right" />
      </AuthProvider>
    </div>
  );
}

export default App;
